// components/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import EntrepriseTools from './EntrepriseTools';
import ToolCard from '../../components/ToolCard';
import Badge from '../../components/Badge';
import UsageChart from '../../components/UsageChart';
import './Dashboard.css';

const Dashboard = () => {
  const { user: currentUser } = useAuth();
  const [usageStats, setUsageStats] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);

  const { planStatus, subscriptionStatus, id, name } = currentUser || {};

  useEffect(() => {
    // Fetch usage stats dynamically
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const res = await fetch('/api/users/usage-stats', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await res.json();
          setUsageStats(data);
        }
      } catch (err) {
        console.error("Dashboard data fetch failed", err);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div id='dashboard' className="dashboard-container text-white p-8 bg-gray-900 rounded-lg shadow-lg min-h-screen">
      {/* Welcome Section */}
      <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
        👋 Welcome back, {name}!
        {planStatus && <Badge variant={planStatus.toLowerCase()}>{planStatus.toUpperCase()}</Badge>}
      </h1>
      <p className="text-sm text-gray-400 mb-6">
        Your User ID: {id} <br />
        Current Plan: {planStatus || 'Free'} ({subscriptionStatus || 'inactive'})
      </p>

      {/* Usage Stats Section */}
      <div className="usage-section mb-8">
        <h2 className="text-xl font-semibold mb-3">📊 Your Weekly Usage</h2>
        {usageStats ? (
          <UsageChart data={usageStats} />
        ) : (
          <p className="text-gray-500">Loading usage data...</p>
        )}
      </div>

      {/* AI Insights Section */}
      <div className="ai-insights bg-gray-800 p-5 rounded-xl shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-3">🤖 RapidMind AI Suggestions</h2>
        {aiInsights.length > 0 ? (
          <ul className="space-y-2 text-sm text-gray-300">
            {aiInsights.map((insight, i) => (
              <li key={i} className="bg-gray-700 p-3 rounded-lg">{insight}</li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No AI insights yet. Start interacting with RapidMind!</p>
        )}
      </div>

      {/* Plan-Based Tools */}
      {subscriptionStatus === 'active' && planStatus === 'Pro' && (
        <div className="pro-tools grid gap-4 md:grid-cols-2">
          <ToolCard title="Full AI Access" description="Use AI without limits." />
          <ToolCard title="Voice Support" description="Convert speech into text instantly." />
          <ToolCard title="Unlimited History" description="Browse all past prompts." />
          <ToolCard title="Export Tools" description="Download results with one click." />
          <ToolCard title="Priority Support" description="Get faster help anytime." />
        </div>
      )}

      {subscriptionStatus === 'active' && planStatus === 'Entreprise' && (
        <div className="enterprise-section">
          <h2 className="text-2xl font-semibold mb-4">🏢 Enterprise Tools</h2>
          <EntrepriseTools />
        </div>
      )}

      {/* Upgrade CTA for Free or Expired */}
      {(!subscriptionStatus || subscriptionStatus === 'new' || subscriptionStatus === 'expired') && (
        <>
          <div className="upgrade-banner bg-gradient-to-r from-indigo-800 to-purple-700 p-6 rounded-xl shadow-lg text-white mb-8 text-center">
            <h2 className="text-2xl font-bold mb-2">🚀 Boost Your Experience</h2>
            <p className="text-sm text-gray-200">Upgrade to unlock AI superpowers and personalized insights.</p>
          </div>

          <div className="plan-comparison grid md:grid-cols-2 gap-6 mt-4">
            {/* Pro Plan */}
            <div className="pro-plan bg-gray-800 rounded-xl p-5 shadow-md border border-indigo-600">
              <h3 className="text-xl font-semibold text-indigo-400 mb-3">Pro Plan 🔥</h3>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>✅ Full AI Access</li>
                <li>✅ Voice Support</li>
                <li>✅ Unlimited History</li>
                <li>✅ Export Tools</li>
                <li>✅ Priority Support</li>
              </ul>
              <button
                onClick={() => window.location.href = '/try-pro'}
                className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-md font-medium"
              >
                Try Pro Now → $19.99/month
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="enterprise-plan bg-gray-800 rounded-xl p-5 shadow-md border border-yellow-500">
              <h3 className="text-xl font-semibold text-yellow-400 mb-3">Entreprise Plan 🏢</h3>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>✅ Custom AI Solutions</li>
                <li>✅ API Integrations</li>
                <li>✅ Team Accounts</li>
                <li>✅ Dedicated Tools</li>
                <li>✅ Premium Support</li>
              </ul>
              <button
                onClick={() => window.location.href = '/entreprise/upgrade'}
                className="mt-4 w-full bg-yellow-500 hover:bg-yellow-600 text-black py-2 rounded-md font-medium"
              >
                Join Entreprise → $99/month
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
